import React, { useState, useEffect } from "react";
import axios from "axios";
import { Document, Page, Text, View, Image, StyleSheet, PDFViewer } from "@react-pdf/renderer";
import { FaSave, FaPlus, FaUpload } from "react-icons/fa";
import LoadTemplate from "./LoadTemplate";
import "react-quill/dist/quill.snow.css";
import api, { API_BASE_URL } from "../../src/utils/api";


const API_COMPANIES = `/companies`;
const API_TEMPLATES = `/letters`;
const API_UPLOAD = `/upload-logo`;

const styles = StyleSheet.create({
  page: { flexDirection: "column", padding: 50, fontSize: 12 },
  headerContainer: { flexDirection: "row", justifyContent: "space-between", marginBottom: 20 },
  logo: { width: 80, height: 80 },
  companyInfo: { textAlign: "right" },
  content: { marginTop: 20, fontSize: 12 },
  footer: { position: "absolute", bottom: 20, left: 40, right: 40, fontSize: 10, textAlign: "justify" },
});

const NotificationLetter = ({ formData }) => (
  <Document>
    <Page size="LETTER" style={styles.page}>
      <View style={styles.headerContainer}>
        {formData.logo && <Image src={formData.logo} style={styles.logo} />}
        <View style={styles.companyInfo}>
          <Text>{formData.companyName}</Text>
          <Text>{formData.companyAddress}</Text>
        </View>
      </View>
      <View style={styles.content}>
        <Text>{formData.layout.replace("{recipientName}", "John Doe")}</Text>
      </View>
      <View style={styles.footer}>
        <Text>{formData.footer}</Text>
      </View>
    </Page>
  </Document>
);

const CoverLetters = () => {
  const [formData, setFormData] = useState({
    id: null,
    name: "",
    layout: "Dear {recipientName},\n\nYour violation details:\n{body}\n\nSincerely,\n{sender}",
    companyName: "",
    companyAddress: "",
    footer: "",
    sender: "",
    senderTitle: "",
    logo: null,
  });

  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    api.get(API_COMPANIES)
      .then(response => setCompanies(response.data))
      .catch(error => console.error("Error fetching companies:", error));
  }, []);

  const handleTemplateLoad = (template) => {
    setFormData(template);
  };

  const handleNewTemplate = () => {
    setFormData(prevState => ({ ...prevState, name: "", layout: "", logo: null }));
  };

  const handleSaveTemplate = () => {
    if (formData.id) {
      // ✅ Update existing template
      api.put(`${API_TEMPLATES}/${formData.id}`, formData)
        .then(() => alert("Template updated successfully!"))
        .catch(error => console.error("Error updating template:", error));
    } else {
      // ✅ Create a new template
      api.post(API_TEMPLATES, formData)
        .then(response => {
          alert("Template saved successfully!");
          setFormData(prevState => ({ ...prevState, id: response.data.id })); // Update ID after creation
        })
        .catch(error => console.error("Error saving template:", error));
    }
  };
  

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const uploadData = new FormData();
    uploadData.append("logo", file);
    try {
      const response = await api.post(API_UPLOAD, uploadData, { headers: { "Content-Type": "multipart/form-data" } });
      setFormData(prevState => ({ ...prevState, logo: `http://47.207.96.12:3200${response.data.filePath}` }));
    } catch (error) {
      console.error("Error uploading logo:", error);
    }
  };

  return (
    <>
      <style>{`
        .container {
          padding: 20px;
          background-color: #f8f9fa;
          min-height: 100vh;
        }

        .page-title {
          font-size: 28px;
          font-weight: bold;
          margin-bottom: 20px;
        }

        .grid-container {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
        }

        .card {
          background: white;
          padding: 20px;
          border-radius: 10px;
          box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1);
          border: 1px solid #ddd;
        }

        .form-title {
          font-size: 20px;
          font-weight: bold;
          margin-bottom: 15px;
        }

        .form-group {
          margin-bottom: 15px;
        }

        .form-group label {
          font-weight: 600;
          display: block;
          margin-bottom: 5px;
        }

        .form-input {
          width: 100%;
          padding: 8px;
          border: 1px solid #ccc;
          border-radius: 5px;
        }

        .form-actions {
          display: flex;
          gap: 10px;
          margin-top: 20px;
        }

        .btn {
          display: flex;
          align-items: center;
          gap: 5px;
          padding: 10px 15px;
          border-radius: 5px;
          cursor: pointer;
        }

        .btn-primary {
          background: #007bff;
          color: white;
        }

        .btn-secondary {
          background: #6c757d;
          color: white;
        }
      `}</style>

      <div className="container">
        <h2 className="page-title">Cover Letters</h2>

        <div className="grid-container">
          {/* Left Column - Form */}
          <div className="card">
            <h3 className="form-title">Create or Load Notification Letter</h3>
            <LoadTemplate onTemplateLoad={handleTemplateLoad} />

            <div className="form-group">
              <label>Template Name</label>
              <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="form-input" placeholder="Enter template name" />
            </div>

            <div className="form-group">
              <label>Company Name</label>
              <select value={formData.companyName} onChange={(e) => setFormData({ ...formData, companyName: e.target.value })} className="form-input">
                <option value="">Select a Company</option>
                {companies.map(company => <option key={company.CompanyID} value={company.Name}>{company.Name}</option>)}
              </select>
            </div>

            <div className="form-group">
              <label>Upload Logo</label>
              <input type="file" accept="image/*" onChange={handleLogoUpload} className="form-input" />
            </div>

            <div className="form-group">
              <label>Layout</label>
              <textarea rows="5" value={formData.layout} onChange={(e) => setFormData({ ...formData, layout: e.target.value })} className="form-input" placeholder="Enter letter content"></textarea>
            </div>

            <div className="form-actions">
              <button onClick={handleNewTemplate} className="btn btn-secondary"><FaPlus /> New Template</button>
              <button onClick={handleSaveTemplate} className="btn btn-primary"><FaSave /> Save Template</button>
            </div>
          </div>

          {/* Right Column - PDF Preview */}
          <div className="card">
            <h3 className="form-title">Preview</h3>
            <PDFViewer style={{ width: "100%", height: "85vh", borderRadius: "8px", border: "1px solid #ccc" }}>
              <NotificationLetter formData={formData} />
            </PDFViewer>
          </div>
        </div>
      </div>
    </>
  );
};

export default CoverLetters;
